import { useCallback, useRef, memo, useState } from 'react';

import type { Group } from '@bugbug/core/types/groups';
import type { SideEffect } from '@bugbug/core/types/utils';
import useModal from '~/hooks/useModal';
import { useAppSelector } from '~/modules/store';
import { useUnlinkComponentFromTest } from '~/modules/tests/hooks/useUnlinkComponentFromTest/useUnlinkComponentFromTest';
import { selectCurrentTest } from '~/modules/tests/tests.selectors';

import { ComponentActionsMenu } from './ComponentActionsMenu';
import * as S from './GroupActions.styled';
import { GroupActionsMenu } from './GroupActionsMenu';

interface GroupActionsProps {
  className?: string;
  group: Group;
  readOnly?: boolean;
  onDelete: SideEffect;
  onRename: SideEffect;
}

export const GroupActions = memo(
  ({ className, group, readOnly = false, onDelete, onRename }: GroupActionsProps) => {
    const modal = useModal();
    const containerRef = useRef<HTMLDivElement>(null);
    const test = useAppSelector(selectCurrentTest);
    const [areActionsLocked, setAreActionsLocked] = useState(false);
    const [handleUnlinkComponent] = useUnlinkComponentFromTest();

    const openDeleteModal = useCallback(
      ({ permanent = false } = {}) =>
        modal.show('delete_group', {
          test,
          group,
          permanent,
          onSubmit: onDelete,
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [modal.show, test, group, onDelete],
    );

    const openCloneModal = useCallback(
      () =>
        modal.show('clone_group', {
          id: group.id,
          testId: test?.id,
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [modal.show, group.id, test?.id],
    );

    const handleUnlink = useCallback(() => {
      if (!test?.id) return;

      handleUnlinkComponent({ testId: test.id, componentId: group.id });
    }, [handleUnlinkComponent, test?.id, group.id]);

    const lockActionButtons = useCallback(() => setAreActionsLocked(true), []);
    const unlockActionButtons = useCallback(() => setAreActionsLocked(false), []);

    return (
      <S.VisibilityWrapper className={className} force={areActionsLocked}>
        <S.Container ref={containerRef} data-testid="GroupActions">
          {!readOnly && !group.isComponent && (
            <GroupActionsMenu
              onClose={unlockActionButtons}
              onOpen={lockActionButtons}
              onDuplicateClick={openCloneModal}
              onRenameClick={onRename}
              onDeleteClick={() => openDeleteModal({ permanent: true })}
              readOnly={readOnly}
            />
          )}
          {!readOnly && group.isComponent && (
            <ComponentActionsMenu
              onClose={unlockActionButtons}
              onOpen={lockActionButtons}
              onRenameClick={onRename}
              onUnlinkClick={handleUnlink}
              onRemoveClick={() => openDeleteModal({ permanent: false })}
              readOnly={readOnly}
            />
          )}
        </S.Container>
      </S.VisibilityWrapper>
    );
  },
);

GroupActions.displayName = 'GroupActions';
